import React from "react";
import {
  validateClaim,
  emptyClaimForm,
  PAYMENT_METHODS,
  FAMILY_CONTACT_CHOICES,
  type ClaimFormInput,
} from "../server/claim-validation.js";
import { ENTITY_TYPES, entityTypeHasNumber } from "../server/entity-types.js";
import { ProgramHeader } from "./ProgramHeader";
import { PhoneInput } from "@mdlegion/shared/components/PhoneInput";

// The claim page. Reached by the durable magic link, which is what authenticates
// it — holding the token proves the sponsor either passed the Channel B name
// gate or was sent a link by staff.
//
// Single page, no wizard: a sponsor is doing one small task and a multi-step
// flow would add ceremony without adding clarity.
//
// Validation imports the SAME validateClaim() the server runs. Not a parallel
// copy — the Round 14 gotcha in this project came from cross-field rules living
// in more than one place, and the identical-gate rule is what prevents the
// client and server ever disagreeing about what is valid.

interface ClaimDetails {
  itemId: string;
  refNumber: string;
  programName: string;
  applicant: string;
  status: string;
  entityType: string;
  entityName: string;
  entityNumber: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  paymentMethod: string;
  notes: string;
  squarePaymentUrl: string;
  squareOffered?: boolean;
  familyContact?: string;
  familyContactOther?: string;
  existingSponsor?: { entity: string; paymentReceived: boolean } | null;
}

type Errors = Partial<Record<keyof ClaimFormInput, string>>;

export function ClaimForm(): React.ReactElement {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [details, setDetails] = React.useState<ClaimDetails | null>(null);
  const [loadError, setLoadError] = React.useState("");
  const [form, setForm] = React.useState<ClaimFormInput>(emptyClaimForm);
  const [errors, setErrors] = React.useState<Errors>({});
  const [busy, setBusy] = React.useState(false);
  const [submitted, setSubmitted] = React.useState("");
  const [underReview, setUnderReview] = React.useState(false);

  React.useEffect(() => {
    if (!token) {
      setLoadError("This link is missing its token. Use the link from your email.");
      return;
    }
    void (async () => {
      try {
        const res = await fetch(`/api/claims/${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((body as { error?: string }).error ?? "Could not load.");
        const d = body as ClaimDetails;
        setDetails(d);
        // Pre-fill from whatever the row already holds, so a sponsor returning
        // after paying on Square does not retype everything.
        setForm((f) => ({
          ...f,
          entityType: d.entityType || "",
          entityName: d.entityName || "",
          entityNumber: d.entityNumber || "",
          contactName: d.contactName || "",
          contactEmail: d.contactEmail || "",
          contactPhone: d.contactPhone || "",
          paymentMethod: d.paymentMethod || "",
          notes: d.notes || "",
          familyContact: d.familyContact || "",
          familyContactOther: d.familyContactOther || "",
        }));
      } catch (e) {
        setLoadError((e as Error).message);
      }
    })();
  }, [token]);

  function set<K extends keyof ClaimFormInput>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    // Clear this field's error as soon as it is touched; the full gate re-runs
    // on submit anyway.
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const check = validateClaim(form, { squareOffered: details?.squareOffered !== false });
    setErrors(check.errors);
    if (!check.ok) return;

    setBusy(true);
    try {
      const res = await fetch(`/api/claims/${encodeURIComponent(token)}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The server runs the identical gate; if it disagrees, its answer wins.
        setErrors((body as { errors?: Errors }).errors ?? {});
        setLoadError((body as { error?: string }).error ?? "Could not submit your claim.");
        return;
      }
      const result = body as { referenceNumber?: string; status?: string };
      // Round 26. Anything other than an explicit "under-review" reads as
      // recorded, so an older server that does not send `status` keeps today's
      // behavior rather than showing a review notice nobody will follow up on.
      setUnderReview(result.status === "under-review");
      setSubmitted(result.referenceNumber ?? details?.refNumber ?? "");
    } catch {
      setLoadError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (loadError && !details) {
    return (
      <><ProgramHeader />
    <main className="mx-auto max-w-xl px-4 py-10">
        <div className="section-card">
          <h1 className="text-xl font-semibold text-program">Sponsorship Claim</h1>
          <p className="mt-3 text-sm text-red-700">{loadError}</p>
        </div>
      </main></>
    );
  }

  if (!details) {
    return (
      <><ProgramHeader />
    <main className="mx-auto max-w-xl px-4 py-10">
        <div className="section-card">
          <p className="text-sm text-slate-600">Loading…</p>
        </div>
      </main></>
    );
  }

  if (submitted) {
    return (
      <><ProgramHeader />
    <main className="mx-auto max-w-xl px-4 py-10">
        <div className="section-card">
          {/*
            Round 26 — two outcomes, and the under-review one says only that.
            It must never mention a conflict, a dispute, or another sponsor:
            the server sends a two-value status precisely so this page cannot
            say more than "we are looking at it". See claim-router.ts.
          */}
          {underReview ? (
            <>
              <h1 className="text-xl font-semibold text-program">
                Thank you — claim received
              </h1>
              <p className="mt-3 text-sm text-slate-700">
                Your sponsorship claim for{" "}
                <strong className="font-mono">{submitted}</strong> has been received and
                is being reviewed.
              </p>
              <p className="mt-3 text-sm text-slate-700">
                We will email you once the review is complete. Nothing further is needed
                from you right now.
              </p>
              <p className="mt-3 text-sm text-slate-700">
                If you have not paid yet, you can still do so — payment is tracked
                separately from this review. Keep the link from your email if you need to
                come back.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-program">
                Thank you — claim recorded
              </h1>
              <p className="mt-3 text-sm text-slate-700">
                Your sponsorship of <strong className="font-mono">{submitted}</strong> is
                recorded.
              </p>
              <p className="mt-3 text-sm text-slate-700">
                If you have not paid yet, you can still do so — your claim is complete
                either way, and payment is tracked separately. Keep the link from your
                email if you need to come back.
              </p>
            </>
          )}
          <p className="field-hint mt-4">
            Sponsoring more than one student? Open this page again using each student's own
            reference number.
          </p>
        </div>
      </main></>
    );
  }

  // A program that does not take card payment does not OFFER it. Showing a
  // non-functional radio would leave a sponsor wondering why a listed
  // payment method does not work; the server enforces the same rule, so
  // this is presentation of a decision rather than the decision itself.
  const squareOffered = details.squareOffered !== false;
  const methods = PAYMENT_METHODS.filter((m) => m !== "Square" || squareOffered);

  const numbered = entityTypeHasNumber(form.entityType);
  const alreadyClaimed = details.status === "claimed";

  return (
    <><ProgramHeader />
    <main className="mx-auto max-w-xl px-4 py-10">
      <div className="section-card">
        <h1 className="text-xl font-semibold text-program">Claim your sponsored student</h1>
        {/* Final confirmation before committing — catches a mis-keyed reference. */}
        <dl className="mt-4 space-y-1 text-sm">
          <div className="flex gap-2">
            <dt className="w-28 shrink-0 text-slate-500">Reference</dt>
            <dd className="font-mono font-semibold">{details.refNumber}</dd>
          </div>
          {details.applicant && (
            <div className="flex gap-2">
              <dt className="w-28 shrink-0 text-slate-500">Applicant</dt>
              <dd className="font-semibold">{details.applicant}</dd>
            </div>
          )}
          {details.programName && (
            <div className="flex gap-2">
              <dt className="w-28 shrink-0 text-slate-500">Program</dt>
              <dd>{details.programName}</dd>
            </div>
          )}
        </dl>
        <p className="field-hint mt-3">
          If this isn't the student you meant to sponsor, stop and check the reference
          number before continuing.
        </p>
      </div>

      {alreadyClaimed && (
        <div className="section-card border-amber-300 bg-amber-50">
          <p className="text-sm text-amber-900">
            This claim has already been submitted. Submitting again will update your
            details.
          </p>
        </div>
      )}

      <form onSubmit={submit} noValidate>
        {/*
          Round 31 - WARN, never block. This page cannot tell a duplicate from
          a legitimate second sponsor, and blocking would strand exactly the
          case the contest machinery exists to handle. One sponsor is named,
          never a list of competitors.
        */}
        {details.existingSponsor && (
          <div className="section-card border-amber-300 bg-amber-50">
            <p className="font-semibold text-amber-900">
              This applicant already has a sponsor
            </p>
            <p className="mt-1 text-sm text-amber-900">
              {details.existingSponsor.entity} — payment{" "}
              {details.existingSponsor.paymentReceived ? "received" : "pending"}.
            </p>
            <p className="mt-2 text-sm text-amber-800">
              You can still continue if you intend to sponsor as well. Your claim will
              be recorded and the program office will confirm which organization is
              the sponsor.
            </p>
          </div>
        )}

        <div className="section-card">
          <h2 className="mb-4 font-semibold text-slate-800">Your organization</h2>

          <div className="mb-4">
            <label className="field-label" htmlFor="entityType">
              Who is sponsoring?
            </label>
            <select
              id="entityType"
              className="field-input"
              value={form.entityType}
              onChange={(e) => set("entityType", e.target.value)}
            >
              <option value="">Choose…</option>
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {errors.entityType && <p className="field-error">{errors.entityType}</p>}
          </div>

          {form.entityType && numbered && (
            <>
              <div className="mb-4">
                <label className="field-label" htmlFor="entityNumber">
                  {form.entityType} number
                </label>
                <input
                  id="entityNumber"
                  className="field-input"
                  inputMode="numeric"
                  placeholder="12"
                  value={form.entityNumber}
                  onChange={(e) => set("entityNumber", e.target.value)}
                />
                {errors.entityNumber && <p className="field-error">{errors.entityNumber}</p>}
              </div>
              <div className="mb-4">
                <label className="field-label" htmlFor="entityName">
                  Name <span className="font-normal text-slate-500">(optional)</span>
                </label>
                <input
                  id="entityName"
                  className="field-input"
                  placeholder="e.g. Hampstead"
                  value={form.entityName}
                  onChange={(e) => set("entityName", e.target.value)}
                />
                {errors.entityName && <p className="field-error">{errors.entityName}</p>}
              </div>
            </>
          )}

          {form.entityType && !numbered && (
            <div className="mb-4">
              <label className="field-label" htmlFor="entityNameOther">
                Who is sponsoring?
              </label>
              <input
                id="entityNameOther"
                className="field-input"
                placeholder="A person's name, or a business"
                value={form.entityName}
                onChange={(e) => set("entityName", e.target.value)}
              />
              {errors.entityName && <p className="field-error">{errors.entityName}</p>}
            </div>
          )}

          <div className="mb-4">
            <label className="field-label" htmlFor="contactName">
              Your name
            </label>
            <input
              id="contactName"
              className="field-input"
              value={form.contactName}
              onChange={(e) => set("contactName", e.target.value)}
            />
            {errors.contactName && <p className="field-error">{errors.contactName}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="contactEmail">
                Your email
              </label>
              <input
                id="contactEmail"
                className="field-input"
                type="email"
                value={form.contactEmail}
                onChange={(e) => set("contactEmail", e.target.value)}
              />
              {errors.contactEmail && <p className="field-error">{errors.contactEmail}</p>}
            </div>
            <div>
              <label className="field-label" htmlFor="contactPhone">
                Your phone
              </label>
              {/* Round 25 — same auto-formatting as the applicant portals. This
                  field did not have their inputMode="tel" bug, but it enforces
                  the identical strict US rule, and a sponsor is at least as
                  likely to be on a phone. addressType is hardcoded "US": a
                  sponsoring entity is always a US Legion organization. */}
              <PhoneInput
                id="contactPhone"
                addressType="US"
                placeholder="410-555-0100"
                value={form.contactPhone}
                onChange={(v) => set("contactPhone", v)}
              />
              {errors.contactPhone && <p className="field-error">{errors.contactPhone}</p>}
            </div>
          </div>
        </div>


        {/*
          Round 30 — its own section between "Your organization" (who you are)
          and Payment. This asks about the sponsor's RELATIONSHIP to the
          student, which is a different question from either, and four radios
          plus a conditional textarea would swamp the contact grid above.
        */}
        <div className="section-card">
          <h2 className="mb-1 font-semibold text-slate-800">
            Your connection to the student
          </h2>
          <p className="field-hint mb-4">
            This helps the program see whether sponsored students have local
            community support as well as financial backing.
          </p>

          <fieldset>
            <legend className="field-label">
              As the prospective sponsor, have you made personal contact with the
              student&apos;s family?
            </legend>
            <div className="mt-2 space-y-1 text-sm">
              {FAMILY_CONTACT_CHOICES.map((c) => (
                <label key={c.code} className="flex items-start gap-2">
                  <input
                    type="radio"
                    className="mt-1"
                    name="familyContact"
                    checked={form.familyContact === c.code}
                    onChange={() => set("familyContact", c.code)}
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {errors.familyContact && <p className="field-error">{errors.familyContact}</p>}

          {form.familyContact === "other" && (
            <div className="mt-3">
              <label className="field-label" htmlFor="familyContactOther">
                Tell us briefly
              </label>
              <textarea
                id="familyContactOther"
                className="field-input"
                rows={2}
                value={form.familyContactOther}
                onChange={(e) => set("familyContactOther", e.target.value)}
              />
              {errors.familyContactOther && (
                <p className="field-error">{errors.familyContactOther}</p>
              )}
            </div>
          )}
        </div>

        <div className="section-card">
          <h2 className="mb-1 font-semibold text-slate-800">Payment</h2>
          <p className="field-hint mb-4">
            Your claim is recorded as soon as you submit — it does not wait on payment.
          </p>

          <div className="mb-4 space-y-1 text-sm">
            {methods.map((m) => (
              <label key={m} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="paymentMethod"
                  checked={form.paymentMethod === m}
                  onChange={() => set("paymentMethod", m)}
                />
                {m === "Square"
                  ? "Pay online by card (Square)"
                  : m === "Check"
                    ? "Check mailed to HQ"
                    : "Other"}
              </label>
            ))}
            {errors.paymentMethod && <p className="field-error">{errors.paymentMethod}</p>}
          </div>

          {form.paymentMethod === "Square" && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-700">
                Pay on the Square page, then come back here and enter the order number from
                your receipt. This link stays valid.
              </p>
              {details.squarePaymentUrl && (
                <p className="mt-3">
                  <a
                    className="btn-secondary"
                    href={details.squarePaymentUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Open the payment page
                  </a>
                </p>
              )}
              <div className="mt-4">
                <label className="field-label" htmlFor="order">
                  Square order number
                </label>
                <input
                  id="order"
                  className="field-input"
                  value={form.squareOrderNumber}
                  onChange={(e) => set("squareOrderNumber", e.target.value)}
                />
                {errors.squareOrderNumber && (
                  <p className="field-error">{errors.squareOrderNumber}</p>
                )}
              </div>
            </div>
          )}

          {(form.paymentMethod === "Check" || form.paymentMethod === "Other") && (
            <div>
              <label className="field-label" htmlFor="paymentNote">
                {form.paymentMethod === "Other"
                  ? "How are you paying?"
                  : "Anything we should know? (optional)"}
              </label>
              <textarea
                id="paymentNote"
                className="field-input"
                rows={2}
                value={form.paymentNote}
                onChange={(e) => set("paymentNote", e.target.value)}
              />
              {errors.paymentNote && <p className="field-error">{errors.paymentNote}</p>}
            </div>
          )}
        </div>

        <div className="section-card">
          <label className="field-label" htmlFor="notes">
            Notes <span className="font-normal text-slate-500">(optional)</span>
          </label>
          <textarea
            id="notes"
            className="field-input"
            rows={3}
            placeholder="e.g. applicant is a member of our SAL Squadron or Junior Auxiliary, or is related to a Post/Unit member"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
          {/*
            Round 32. The old copy invited a sponsor to list other students'
            reference numbers HERE, which read as a way to claim them. It is
            not: a student named only in this box has no claim row, so neither
            conflict detection (Round 27) nor the contest card (Round 28) ever
            sees them, and a second Post can claim that student with no warning
            to anyone. The field's real purpose — flagging one payment that
            covers several students, for reconciliation — is kept, and the
            second sentence says plainly that a reference number typed here
            claims nothing.

            The PLACEHOLDER deliberately no longer shows a combined payment.
            It sits in the box and reads as an example to copy, so it carries
            more weight than the hint below it — and any example built on "one
            check covers 5 students, refs ..." has to drag a caveat along with
            it to stop a skimmer reading the list of references as the claim.
            An example on a different subject entirely needs no caveat, and
            leaves the combined-payment case to the hint below, where it can be
            stated properly. Do not put reference numbers back in here.

            Two examples, one per program: this ONE form serves both, so a
            single Boys-flavored example reads as not-for-you to half the
            sponsors who see it.

            The third example — a relative of a Post/Unit member — is here
            because this box is not only payment reconciliation. A note like
            that is exactly the context a director weighs when two
            organizations claim the same student, and ClaimCard renders Notes
            immediately above the resolve options, so what a sponsor writes
            here lands in front of the person making that decision.
          */}
          <p className="field-hint">
            Anything else the program director should know — for example, if one payment
            covers more than one student, list their reference numbers here so we can
            match it up. Each student still needs their own claim submitted separately;
            listing a reference number here does not claim them.
          </p>
          {errors.notes && <p className="field-error">{errors.notes}</p>}
        </div>

        {loadError && <p className="field-error mb-3">{loadError}</p>}

        <button className="btn-primary w-full" type="submit" disabled={busy}>
          {busy ? "Submitting…" : "Submit my claim"}
        </button>
      </form>
    </main></>
  );
}
